import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../common';
import { EdiService, formatCents, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiBatch } from '../../../dtos/billing/edi.dto';
import BatchDetail from './BatchDetail';
import SendModeSwitch from './SendModeSwitch';
import { Cell, ErrorNote, errorText } from './ediUi';

const STATUS_LABEL: Record<string, string> = {
  AWAITING_SEND: 'Awaiting send',
  SENDING: 'Sending',
  SENT: 'Sent',
  HELD: 'Held',
  PARTIAL: 'Partly sent',
};

/** SUB-03 batches: list, then a batch's claims to send or hold. */
const BatchesView: React.FC = () => {
  const [batches, setBatches] = useState<EdiBatch[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (openId) return undefined;
    let cancelled = false;
    EdiService.listBatches()
      .then((b) => { if (!cancelled) setBatches(b); })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load batches')); });
    return () => { cancelled = true; };
  }, [openId]);

  if (openId) return <BatchDetail batchId={openId} onBack={() => setOpenId(null)} />;

  return (
    <div className="space-y-3 p-3 sm:p-4" aria-label="Batches">
      <SendModeSwitch />
      <ErrorNote message={error} />
      {!batches && !error && <LoadingSpinner label="Loading batches" />}
      {batches && batches.length === 0 && <p className="text-sm text-slate-500">No batches yet. Batches are built each night from ready visits.</p>}
      {batches && batches.length > 0 && (
        <div className="text-sm">
          <div className="hidden grid-cols-5 gap-2 border-b border-slate-100 pb-1 text-xs uppercase text-slate-500 sm:grid">
            <span>Built</span><span>Status</span><span className="text-right">Claims</span><span className="text-right">Accepted / rejected</span><span className="text-right">Projected charge</span>
          </div>
          {batches.map((b) => (
            <button
              key={b.batchId}
              type="button"
              onClick={() => setOpenId(b.batchId)}
              aria-label={`Open batch of ${formatEdiDate(b.createdAt)}`}
              className="grid w-full grid-cols-1 gap-1 border-b border-slate-100 py-2 text-left hover:bg-slate-50 sm:grid-cols-5 sm:gap-2"
            >
              <Cell label="Built" className="font-medium">{formatEdiDate(b.createdAt)}</Cell>
              <Cell label="Status">{STATUS_LABEL[b.status] || b.status}</Cell>
              <Cell label="Claims" className="sm:text-right">{b.counts.claims}{b.heldClaimIds.length ? ` (${b.heldClaimIds.length} held)` : ''}</Cell>
              <Cell label="Accepted / rejected" className="sm:text-right">{b.counts.accepted} / {b.counts.rejected}</Cell>
              <Cell label="Projected charge" className="sm:text-right">{b.chargeableCount > 0 ? formatCents(b.projectedChargeCents) : '—'}</Cell>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BatchesView;
